
import { Link } from "react-router";
import { Heart } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { SOCIALS, SOCIAL_ORDER } from "@/lib/social";
import { useFeatures } from "@/lib/features";

export default function Footer({ dark = false }: { dark?: boolean }) {
  const theme = usePalette();
  const { features } = useFeatures();
  const quickLinks = [
    { label: "About", href: "/about" },
    { label: "Videos", href: "/videos" },
    ...(features.guides ? [{ label: "Guides", href: "/guides" }] : []),
    { label: "My Favorites", href: "/favorites" },
    ...(features.shop ? [{ label: "Shop", href: "/shop" }] : []),
    { label: "Retreats", href: "/retreats" },
    { label: "Connect", href: "/#connect" },
    { label: "Newsletter", href: "/#newsletter" },
  ];
  return (
    <footer style={{ backgroundColor: dark ? "#0c0d12" : colors.navy, color: colors.cream }}>
      <div
        className="h-1"
        style={{
          background: dark ? "#665371" : `linear-gradient(to right, ${theme.accent}, ${theme.warm}, ${theme.pop})`,
        }}
      />

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <Link to="/" className="mb-3 inline-block" aria-label="A Few Loose Threads — home">
              <img
                src="/logos/text-logo-purple.png"
                alt="A Few Loose Threads"
                className="h-12 w-auto"
                width={500}
                height={185}
                style={dark ? { filter: "brightness(0) invert(1)", opacity: 0.85 } : undefined}
              />
            </Link>
            {!dark && <p
              className="text-xs tracking-widest uppercase mb-3"
              style={{
                fontFamily: fonts.body,
                fontWeight: 600,
                color: theme.warm,
              }}
            >
              Loose Threads, Big Ideas
            </p>}
            <p
              className="text-sm leading-relaxed opacity-60"
              style={{ fontFamily: fonts.body, letterSpacing: "0.03em" }}
            >
              Creating, crafting, and celebrating the creative everyday. Thanks
              for being part of this community.
            </p>
          </div>

          <div>
            <h4
              className={dark ? "text-lg mb-4" : "text-xs tracking-[0.2em] uppercase mb-4"}
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                color: dark ? "#c8bed7" : theme.accentSoft,
              }}
            >
              Quick Links
            </h4>
            <div className="flex flex-col gap-2">
              {quickLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-sm opacity-70 hover:opacity-100 transition-opacity tracking-wide cursor-pointer"
                  style={{ fontFamily: fonts.body, letterSpacing: "0.03em" }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4
              className={dark ? "text-lg mb-4" : "text-xs tracking-[0.2em] uppercase mb-4"}
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                color: dark ? "#c8bed7" : theme.accentSoft,
              }}
            >
              Follow Along
            </h4>
            <div className="flex flex-col gap-2">
              {SOCIAL_ORDER.map((key) => {
                const s = SOCIALS[key];
                return (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm opacity-70 hover:opacity-100 transition-opacity tracking-wide cursor-pointer"
                    style={{ fontFamily: fonts.body, letterSpacing: "0.03em" }}
                  >
                    {s.platform}
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p
            className="text-xs opacity-40 flex items-center gap-1"
            style={{ fontFamily: fonts.body }}
          >
            Made with{" "}
            <Heart
              size={12}
              style={{ color: colors.berryPink }}
              fill={colors.berryPink}
            />{" "}
            by Chelsea
          </p>
          <p
            className="text-xs opacity-40"
            style={{ fontFamily: fonts.body }}
          >
            &copy; {new Date().getFullYear()} A Few Loose Threads. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
